import { useRef, useEffect, useState, FormEvent } from 'react';

export default function Contact() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Monta a mensagem e abre o WhatsApp em nova aba
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    // Impede o comportamento padrão do formulário (sem reload, sem fetch)
    e.preventDefault();

    const form = e.currentTarget;
    const nome = (form.elements.namedItem('nome') as HTMLInputElement).value.trim();
    const email = (form.elements.namedItem('email') as HTMLInputElement).value.trim();
    const area = (form.elements.namedItem('area') as HTMLSelectElement).value;
    const mensagem = (form.elements.namedItem('mensagem') as HTMLTextAreaElement).value.trim();

    // Monta a mensagem no formato exato solicitado
    const texto =
      `Nome Completo: ${nome} | E-mail: ${email} | Área do Caso: ${area} | Motivo do Contato: ${mensagem}`;

    // Codifica para URL e monta o link do WhatsApp
    const encoded = encodeURIComponent(texto);
    const whatsappUrl = `https://wa.me/5567999210516?text=${encoded}`;

    // Feedback visual rápido antes de abrir
    setOpening(true);
    setTimeout(() => {
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      setOpening(false);
    }, 400);
  };

  return (
    <section id="contato" ref={ref} className="py-24 bg-[#1F2A44]">
      <div className="max-w-7xl mx-auto px-4 md:px-10">

        {/* Cabeçalho da seção */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center justify-center mb-4">
            <div className="h-px w-10 bg-[#C8B07A] mr-4"></div>
            <span className="text-[#C8B07A] text-xs font-semibold tracking-[0.3em] uppercase">Fale Conosco</span>
            <div className="h-px w-10 bg-[#C8B07A] ml-4"></div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-display mb-4">
            Entre em Contato
          </h2>
          <p className="text-white/50 max-w-xl mx-auto text-sm leading-relaxed">
            Preencha o formulário e você será direcionado ao WhatsApp com a mensagem já pronta.
          </p>
        </div>

        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-10 transition-all duration-1000 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Formulário */}
          <div className="bg-[#2C3756] rounded-lg p-8">
            <h3 className="text-white font-semibold text-lg mb-6">Envie sua mensagem</h3>

            <form
              id="contato-whatsapp"
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              {/* Nome Completo */}
              <div>
                <label className="text-white/60 text-xs mb-1.5 block">Nome Completo *</label>
                <input
                  type="text"
                  name="nome"
                  required
                  placeholder="Seu nome completo"
                  className="w-full bg-[#1F2A44] border border-white/10 focus:border-[#C8B07A] text-white placeholder-white/30 text-sm px-4 py-3 rounded-md outline-none transition-colors duration-300"
                />
              </div>

              {/* E-mail */}
              <div>
                <label className="text-white/60 text-xs mb-1.5 block">E-mail *</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="seu@email.com"
                  className="w-full bg-[#1F2A44] border border-white/10 focus:border-[#C8B07A] text-white placeholder-white/30 text-sm px-4 py-3 rounded-md outline-none transition-colors duration-300"
                />
              </div>

              {/* Área do Caso */}
              <div>
                <label className="text-white/60 text-xs mb-1.5 block">Área do Caso *</label>
                <select
                  name="area"
                  required
                  defaultValue=""
                  className="w-full bg-[#1F2A44] border border-white/10 focus:border-[#C8B07A] text-white text-sm px-4 py-3 rounded-md outline-none transition-colors duration-300 cursor-pointer appearance-none"
                >
                  <option value="" disabled className="text-white/30">Selecione uma área</option>
                  <option value="Direito Trabalhista" className="text-white bg-[#1F2A44]">Direito Trabalhista</option>
                  <option value="Direito Civil" className="text-white bg-[#1F2A44]">Direito Civil</option>
                  <option value="Direito Previdenciário" className="text-white bg-[#1F2A44]">Direito Previdenciário</option>
                  <option value="Direito de Família" className="text-white bg-[#1F2A44]">Direito de Família</option>
                  <option value="Outro" className="text-white bg-[#1F2A44]">Outro</option>
                </select>
              </div>

              {/* Mensagem */}
              <div>
                <label className="text-white/60 text-xs mb-1.5 flex justify-between">
                  <span>Mensagem *</span>
                  <span className={charCount > 500 ? 'text-red-400' : 'text-white/40'}>{charCount}/500</span>
                </label>
                <textarea
                  name="mensagem"
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="Descreva brevemente sua situação ou dúvida..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-[#1F2A44] border border-white/10 focus:border-[#C8B07A] text-white placeholder-white/30 text-sm px-4 py-3 rounded-md outline-none transition-colors duration-300 resize-none"
                />
              </div>

              {/* Botão — abre o WhatsApp */}
              <button
                type="submit"
                disabled={opening || charCount > 500}
                className="inline-flex items-center justify-center gap-2 bg-[#C8B07A] hover:bg-[#D6C18F] disabled:opacity-60 text-[#1F2A44] px-6 py-3.5 rounded-md font-bold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap mt-1"
              >
                {opening ? (
                  <>
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-loader-4-line text-base animate-spin"></i>
                    </span>
                    Abrindo WhatsApp...
                  </>
                ) : (
                  <>
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-whatsapp-line text-base"></i>
                    </span>
                    Enviar via WhatsApp
                  </>
                )}
              </button>

              <p className="text-white/30 text-xs text-center leading-relaxed">
                Ao clicar, você será redirecionado ao WhatsApp com a mensagem já preenchida.
              </p>
            </form>
          </div>

          {/* Informações de contato + mapa */}
          <div className="flex flex-col gap-6">
            <div className="bg-[#2C3756] rounded-lg p-8 flex flex-col gap-5">
              <h3 className="text-white font-semibold text-lg mb-1">Informações de Contato</h3>

              <a
                href="https://wa.me/5567999210516"
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-[#1F2A44] group-hover:bg-[#C8B07A] rounded-lg transition-colors duration-300 flex-shrink-0">
                  <i className="ri-whatsapp-line text-[#C8B07A] group-hover:text-[#1F2A44] text-xl transition-colors duration-300"></i>
                </div>
                <div>
                  <div className="text-white/50 text-xs mb-0.5">WhatsApp</div>
                  <div className="text-white text-sm font-medium group-hover:text-[#C8B07A] transition-colors duration-300">+55 67 99921-0516</div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#1F2A44] rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-2-line text-[#C8B07A] text-xl"></i>
                </div>
                <div>
                  <div className="text-white/50 text-xs mb-0.5">Endereço</div>
                  <div className="text-white text-sm font-medium leading-relaxed">
                    Av. Afonso Pena, 2440 – Sala 123<br />
                    Centro – Campo Grande/MS<br />
                    CEP: 79002-074
                  </div>
                </div>
              </div>

              <a
                href="https://www.instagram.com/jocimartadiotoadv/"
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-[#1F2A44] group-hover:bg-[#C8B07A] rounded-lg transition-colors duration-300 flex-shrink-0">
                  <i className="ri-instagram-line text-[#C8B07A] group-hover:text-[#1F2A44] text-xl transition-colors duration-300"></i>
                </div>
                <div>
                  <div className="text-white/50 text-xs mb-0.5">Instagram</div>
                  <div className="text-white text-sm font-medium group-hover:text-[#C8B07A] transition-colors duration-300">@jocimartadiotoadv</div>
                </div>
              </a>
            </div>

            {/* Google Maps */}
            <div className="rounded-lg overflow-hidden flex-1 min-h-[200px]">
              <iframe
                title="Localização - Jocimar Tadioto Advocacia"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3736.042!2d-54.6167!3d-20.4697!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9486e865b6f0b9c7%3A0x0!2sAv.+Afonso+Pena%2C+2440+-+Centro%2C+Campo+Grande+-+MS!5e0!3m2!1spt!2sbr!4v1711464000000"
                width="100%"
                height="240"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                aria-label="Mapa - Av. Afonso Pena, 2440, Campo Grande/MS"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
